import { useCallback, useEffect, useRef, useState } from 'react';
import { store } from '@store/index';
import { consultationApi } from '@services/consultationApi';
import type { ConsultationHistoryQuery, ConsultationMessage, ConsultationScope, ConsultationSummary } from '#types/consultation';

export function mergeMessages(current: ConsultationMessage[], incoming: ConsultationMessage[]): ConsultationMessage[] {
  const byId = new Map(current.map((message) => [message.id, message]));
  incoming.forEach((message) => byId.set(message.id, message));
  return [...byId.values()].sort((a, b) => BigInt(a.id) < BigInt(b.id) ? -1 : BigInt(a.id) > BigInt(b.id) ? 1 : 0);
}

export function useConsultationChat(scope: ConsultationScope, active: boolean) {
  const { authEpoch, conversationUuid } = scope;
  const [messages, setMessages] = useState<ConsultationMessage[]>([]);
  const [conversation, setConversation] = useState<ConsultationSummary | null>(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const newest = useRef('0');
  const oldest = useRef<string>();
  const initialized = useRef(false);
  const generation = useRef(0);
  const running = useRef<Promise<void> | null>(null);
  const requestRef = useRef<{ abort(): void }>();
  const activeRef = useRef(active);
  activeRef.current = active;
  const alive = useCallback((version: number) => version === generation.current && store.getState().user.authEpoch === authEpoch, [authEpoch]);

  const load = useCallback((older = false): Promise<void> => {
    if (!activeRef.current || store.getState().user.authEpoch !== authEpoch) return Promise.resolve();
    if (running.current) return running.current;
    const version = generation.current;
    const operation = (async () => {
      setBusy(true);
      try {
        let after = newest.current;
        do {
          const query: ConsultationHistoryQuery = { authEpoch, conversationUuid };
          if (older) query.beforeId = oldest.current;
          else if (initialized.current) query.afterId = after;
          const request = store.dispatch(consultationApi.endpoints.consultationHistory.initiate(query, { subscribe: false, forceRefetch: true }));
          requestRef.current = request;
          const timeout = window.setTimeout(() => request.abort(), 15000);
          const data = await request.unwrap().finally(() => {
            window.clearTimeout(timeout);
            if (requestRef.current === request) requestRef.current = undefined;
          });
          if (!alive(version)) return;
          setMessages((previous) => mergeMessages(previous, data.messages));
          if (!older) setConversation(data.conversation);
          if (older || !initialized.current) {
            setHasOlder(data.hasMore);
            if (data.messages.length) oldest.current = data.messages[0].id;
          }
          if (older) break;
          const wasInitialized = initialized.current;
          initialized.current = true;
          if (data.messages.length) newest.current = data.messages[data.messages.length - 1].id;
          if (!wasInitialized || !data.hasMore) break;
          after = newest.current;
        } while (alive(version));
        if (alive(version)) setError(false);
      } catch (failure) {
        if (alive(version)) { setError(true); throw failure; }
      } finally {
        if (alive(version)) setBusy(false);
      }
    })();
    const settled = operation.finally(() => { if (running.current === settled) running.current = null; });
    running.current = settled;
    return settled;
  }, [authEpoch, conversationUuid, alive]);

  const refresh = useCallback(async () => {
    // A send finishing during an earlier poll must also fetch the messages after that poll.
    if (running.current) await running.current.catch(() => {});
    await load();
  }, [load]);

  useEffect(() => {
    activeRef.current = active;
    if (!active) return;
    const poll = () => { if (!document.hidden && document.hasFocus()) void load().catch(() => {}); };
    void load().catch(() => {});
    const timer = window.setInterval(poll, 5000);
    window.addEventListener('focus', poll);
    document.addEventListener('visibilitychange', poll);
    return () => {
      activeRef.current = false;
      generation.current += 1;
      requestRef.current?.abort();
      running.current = null;
      window.clearInterval(timer);
      window.removeEventListener('focus', poll);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [active, load]);

  return { messages, conversation, hasOlder, busy, error, refresh, loadOlder: () => load(true),
    appendSent: (message: ConsultationMessage) => setMessages((previous) => mergeMessages(previous, [message])) };
}
